(() => {
  if (customElements.get('nirobi-product-stories')) return;

  class NirobiProductStories extends HTMLElement {
    connectedCallback() {
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      cancelAnimationFrame(this.scrollFrame);
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.releaseFullscreen();
      if (this.dialog?.open) this.dialog.close();
      this.clearPlayer();
      this.setMenu(false);
      this.triggers?.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      if (this.error) this.error.hidden = true;
      this.origin = null;
      if (this.overlayHost) {
        this.append(this.dialog);
        this.overlayHost.remove();
        this.overlayHost = null;
      }
      this.classList.remove('is-ready');
    }

    setup() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.events = new AbortController();
      const { signal } = this.events;
      this.track = this.querySelector('[data-stories-track]');
      this.dialog = this.querySelector('dialog');
      this.closeButton = this.querySelector('[data-story-close]');
      this.player = this.querySelector('[data-story-player]');
      this.caption = this.querySelector('[data-story-caption]');
      this.progress = this.querySelector('[data-story-progress]');
      this.toggle = this.querySelector('[data-story-toggle]');
      this.resume = this.querySelector('[data-story-resume]');
      this.mute = this.querySelector('[data-story-mute]');
      this.fullscreenButton = this.querySelector('[data-story-fullscreen]');
      this.stage = this.querySelector('[data-story-stage]');
      this.status = this.querySelector('[data-story-status]');
      this.shop = this.querySelector('[data-story-shop]');
      this.shopLinks = [...this.querySelectorAll('[data-story-shop]')];
      this.menuButton = this.querySelector('[data-story-menu]');
      this.menu = this.querySelector('[data-story-menu-panel]');
      this.detailsButton = this.querySelector('[data-story-details]');
      this.detailsPanel = this.querySelector('[data-story-details-panel]');
      this.share = this.querySelector('[data-story-share]');
      this.error = this.querySelector('[data-story-error]');
      this.previous = this.querySelector('[data-stories-prev]');
      this.next = this.querySelector('[data-stories-next]');
      this.back = this.querySelector('[data-story-back]');
      this.forward = this.querySelector('[data-story-forward]');
      this.triggers = [...this.querySelectorAll('[data-story-trigger]')];
      if (!this.track || !this.dialog || !this.player || !this.caption) return;
      this.rail = this.track.closest('.nirobi-stories__rail');
      this.items = [...this.track.querySelectorAll('.nirobi-stories__item')];
      this.modalMedia = window.matchMedia('(max-width: 749px), (pointer: coarse) and (max-height: 500px) and (orientation: landscape)');
      this.modalMedia.addEventListener('change', () => this.updatePresentation(), { signal });
      document.addEventListener('nirobi:story-open', event => {
        if (event.detail?.owner !== this && this.dialog.open) this.closeStory(false);
      }, { signal });

      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => this.showStory(index), { signal });
      });
      this.previous?.addEventListener('click', () => this.scrollRail(-1), { signal });
      this.next?.addEventListener('click', () => this.scrollRail(1), { signal });
      this.back?.addEventListener('click', () => this.showStory(this.index - 1), { signal });
      this.forward?.addEventListener('click', () => this.showStory(this.index + 1), { signal });
      this.querySelector('[data-story-close]')?.addEventListener('click', () => this.closeStory(), { signal });
      this.toggle?.addEventListener('click', () => {
        this.togglePlayback();
        this.setMenu(false, true);
      }, { signal });
      this.resume?.addEventListener('click', () => {
        this.togglePlayback();
        (this.menuButton || this.toggle)?.focus({ preventScroll: true });
      }, { signal });
      this.mute?.addEventListener('click', () => {
        if (this.video) {
          this.muted = !this.video.muted;
          this.video.muted = this.muted;
          this.updatePlayback();
        }
      }, { signal });
      this.fullscreenButton?.addEventListener('click', () => {
        this.setMenu(false, true);
        this.toggleFullscreen();
      }, { signal });
      this.menuButton?.addEventListener('click', () => this.setMenu(this.menu.hidden, true), { signal });
      document.addEventListener('pointerdown', event => {
        if (this.menu && !this.menu.hidden && !this.menu.contains(event.target) && !this.menuButton.contains(event.target)) {
          this.setMenu(false);
        }
      }, { signal });
      document.addEventListener('focusin', event => {
        if (this.menu && !this.menu.hidden && !this.menu.contains(event.target) && event.target !== this.menuButton) {
          this.setMenu(false);
        }
      }, { signal });
      this.detailsButton?.addEventListener('click', () => {
        const show = this.detailsPanel.hidden;
        this.detailsPanel.hidden = !show;
        this.detailsButton.setAttribute('aria-expanded', String(show));
        this.detailsButton.setAttribute('aria-label', show ? 'Hide product details' : 'Show product details');
      }, { signal });
      document.addEventListener('fullscreenchange', () => {
        const active = document.fullscreenElement === this.stage;
        if (this.wasFullscreen && !active) {
          this.expanded = false;
          this.updatePresentation();
        }
        this.wasFullscreen = active;
        this.updateFullscreenButton();
      }, { signal });
      this.share?.addEventListener('click', () => this.shareProduct(), { signal });
      if (this.share) this.share.hidden = !this.shop;
      this.shopLinks.forEach(link => link.addEventListener('click', async event => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const target = document.getElementById(this.dataset.storyBuyTarget);
        if (!target) return;
        event.preventDefault();
        await this.closeStory();
        target.scrollIntoView({ behavior: this.motion(), block: 'center' });
        if (!target.disabled) target.focus({ preventScroll: true });
      }, { signal }));
      this.player.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
        this.player.setPointerCapture(event.pointerId);
      }, { signal });
      this.player.addEventListener('pointercancel', () => { this.gesture = null; }, { signal });
      this.player.addEventListener('pointerup', event => {
        const gesture = this.gesture;
        this.gesture = null;
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (Math.abs(dy) >= 55 && Math.abs(dy) > Math.abs(dx) * 1.3) {
          this.showStory(this.index + (dy < 0 ? 1 : -1));
        } else if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
          this.togglePlayback();
        }
      }, { signal });

      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.updateRail());
      }, { signal, passive: true });
      this.track.addEventListener('keydown', event => this.onRailKeydown(event), { signal });
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        if (this.menu && !this.menu.hidden) this.setMenu(false, true);
        else if (this.expanded) this.exitFullscreen();
        else this.closeStory();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.finishClose();
      }, { signal });
      this.dialog.addEventListener('pointerdown', event => {
        this.backdropPress = this.isBackdropEvent(event);
      }, { signal });
      this.dialog.addEventListener('click', event => {
        if (this.backdropPress && this.isBackdropEvent(event)) this.closeStory();
        this.backdropPress = false;
      }, { signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'Escape' && this.menu && !this.menu.hidden) {
          event.preventDefault();
          this.setMenu(false, true);
          return;
        }
        if (this.menu?.contains(event.target)) return;
        if (event.key === 'Escape' && this.dialog.dataset.presentation === 'floating') {
          event.preventDefault();
          this.closeStory();
          return;
        }
        if (event.target.closest('input, select, textarea')) return;
        if (event.key === ' ' && event.target === this.dialog) {
          event.preventDefault();
          this.togglePlayback();
          return;
        }
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const vertical = event.key === 'ArrowUp' || event.key === 'ArrowDown';
        const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        this.showStory(this.index + (!vertical && this.isRtl() ? -direction : direction));
      }, { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateRail());
        this.resizeObserver.observe(this.track);
        if (this.rail) this.resizeObserver.observe(this.rail);
        this.triggers.forEach(trigger => this.resizeObserver.observe(trigger));
      } else {
        window.addEventListener('resize', () => this.updateRail(), { signal });
      }
      this.classList.add('is-ready');
      this.updateRail();
    }

    isRtl() {
      return getComputedStyle(this.track).direction === 'rtl';
    }

    setMenu(open, focus = false) {
      if (!this.menu || !this.menuButton) return;
      this.menu.hidden = !open;
      this.menuButton.setAttribute('aria-expanded', String(open));
      if (focus) (open ? this.toggle : this.menuButton)?.focus({ preventScroll: true });
    }

    motion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    updateRail() {
      const trackStyle = getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.columnGap) || 0;
      const contentWidth = this.items.reduce((width, item) => width + item.offsetWidth, 0) +
        gap * Math.max(0, this.items.length - 1);
      const railStyle = getComputedStyle(this.rail || this.track);
      const availableWidth = (this.rail || this.track).clientWidth -
        (parseFloat(railStyle.paddingLeft) || 0) - (parseFloat(railStyle.paddingRight) || 0);
      // Measure the whole rail, independent of the arrows' current visibility.
      // A ResizeObserver callback must not make controls create their own overflow.
      const overflow = contentWidth > availableWidth + 2;
      if (this.previous && this.previous.hidden === overflow) this.previous.hidden = !overflow;
      if (this.next && this.next.hidden === overflow) this.next.hidden = !overflow;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.max(0, this.isRtl() ? -this.track.scrollLeft : this.track.scrollLeft);
      const edgeTolerance = Math.max(2, parseFloat(trackStyle.paddingLeft) || 0, parseFloat(trackStyle.paddingRight) || 0) + 2;
      if (this.previous) {
        this.previous.disabled = !overflow || position <= edgeTolerance;
      }
      if (this.next) {
        this.next.disabled = !overflow || position >= maximum - edgeTolerance;
      }
    }

    scrollRail(direction) {
      const card = this.triggers[0]?.closest('.nirobi-stories__item');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 12;
      const step = (card?.getBoundingClientRect().width || 96) + gap;
      this.track.scrollBy({ left: direction * step * (this.isRtl() ? -1 : 1), behavior: this.motion() });
    }

    onRailKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const trigger = event.target.closest('[data-story-trigger]');
      if (!trigger) return;
      const current = this.triggers.indexOf(trigger);
      let target = current;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        target += this.isRtl() ? -direction : direction;
      } else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = this.triggers.length - 1;
      else return;
      event.preventDefault();
      target = Math.max(0, Math.min(this.triggers.length - 1, target));
      const nextTrigger = this.triggers[target];
      nextTrigger.focus({ preventScroll: true });
      const itemBounds = nextTrigger.getBoundingClientRect();
      const trackBounds = this.track.getBoundingClientRect();
      if (itemBounds.left < trackBounds.left) {
        this.track.scrollBy({ left: itemBounds.left - trackBounds.left - 5, behavior: this.motion() });
      } else if (itemBounds.right > trackBounds.right) {
        this.track.scrollBy({ left: itemBounds.right - trackBounds.right + 5, behavior: this.motion() });
      }
    }

    isBackdropEvent(event) {
      if (event.target !== this.dialog) return false;
      const bounds = this.dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom;
    }

    showStory(index) {
      if (index < 0 || index >= this.triggers.length) return;
      const trigger = this.triggers[index];
      const templateId = trigger.dataset.storyTemplate;
      const template = [...this.querySelectorAll('template')].find(item => item.id === templateId);
      if (!template) return;
      const content = template.content.cloneNode(true);
      const video = content.querySelector('video');
      if (!video) return;

      const opening = !this.dialog.open;
      if (opening) this.origin = trigger;
      this.clearPlayer();
      this.index = index;
      this.video = video;
      this.caption.textContent = content.querySelector('[data-story-title]')?.textContent.trim() ||
        trigger.getAttribute('aria-label') || `Video ${index + 1}`;
      video.setAttribute('aria-label', this.caption.textContent);
      video.controls = false;
      video.playsInline = true;
      video.autoplay = false;
      video.muted = this.muted ?? false;
      if (this.status) this.status.hidden = true;
      this.needsPlay = false;
      if (this.error) this.error.hidden = true;
      this.mediaEvents = new AbortController();
      for (const eventName of ['play', 'pause', 'volumechange', 'loadedmetadata', 'timeupdate']) {
        video.addEventListener(eventName, () => this.updatePlayback(), { signal: this.mediaEvents.signal });
      }
      video.addEventListener('ended', () => {
        if (this.index < this.triggers.length - 1) this.showStory(this.index + 1);
        else this.updatePlayback();
      }, { signal: this.mediaEvents.signal });
      video.addEventListener('error', () => {
        if (this.error) this.error.hidden = false;
        this.updatePlayback();
      }, { signal: this.mediaEvents.signal });
      const sources = [...video.querySelectorAll('source')];
      const failedSources = new Set();
      sources.forEach(source => source.addEventListener('error', () => {
        failedSources.add(source);
        if (failedSources.size === sources.length && video.readyState === 0 && this.error) {
          this.error.hidden = false;
          this.updatePlayback();
        }
      }, { signal: this.mediaEvents.signal }));
      this.player.replaceChildren(video);
      this.updatePlayback();
      this.triggers.forEach((item, position) => item.setAttribute('aria-expanded', String(position === index)));
      // Keep navigation controls focusable so changing a video never discards focus.
      this.back?.setAttribute('aria-disabled', String(index === 0));
      this.forward?.setAttribute('aria-disabled', String(index === this.triggers.length - 1));
      if (this.back) this.back.hidden = false;
      if (this.forward) this.forward.hidden = false;
      if (opening) {
        document.dispatchEvent(new CustomEvent('nirobi:story-open', { detail: { owner: this } }));
        this.mountDialog();
        this.updatePresentation(true);
        this.closeButton?.focus({ preventScroll: true });
      }
      this.updateFullscreenButton();
      const playback = video.play();
      playback?.catch(error => {
        if (video !== this.video || !this.dialog.open) return;
        if (error.name === 'NotAllowedError' && this.status) {
          this.needsPlay = true;
          this.status.textContent = 'Tap Play to start this video.';
          this.status.hidden = false;
        }
        this.updatePlayback();
      });
    }

    togglePlayback() {
      if (!this.video) return;
      if (this.video.paused || this.video.ended) {
        this.video.play()?.catch(() => this.updatePlayback());
      } else this.video.pause();
    }

    mountDialog() {
      if (this.overlayHost) return;
      const host = document.createElement('div');
      host.className = 'nirobi-stories nirobi-stories__overlay-host';
      const style = getComputedStyle(this);
      host.style.setProperty('--story-accent', style.getPropertyValue('--story-accent'));
      host.style.fontFamily = style.fontFamily;
      host.style.fontSize = style.fontSize;
      document.body.append(host);
      host.append(this.dialog);
      this.overlayHost = host;
    }

    updatePresentation(open = false) {
      const presentation = this.expanded || this.modalMedia.matches ? 'modal' : 'floating';
      this.dialog.dataset.expanded = String(Boolean(this.expanded));
      this.updateFullscreenButton();
      if (this.dialog.dataset.presentation === presentation && !open) return;
      const wasOpen = this.dialog.open;
      const focused = document.activeElement;
      const restoreControl = focused && this.dialog.contains(focused);
      // Close and immediately reopen the same element to change native dialog mode.
      // The queued close event sees it open and leaves the playing video intact.
      if (wasOpen) this.dialog.close();
      this.dialog.dataset.presentation = presentation;
      this.dialog.setAttribute('aria-modal', String(presentation === 'modal'));
      if (wasOpen || open) {
        if (presentation === 'modal') this.dialog.showModal();
        else this.dialog.show();
        if (restoreControl) focused.focus({ preventScroll: true });
        else if (presentation === 'modal') this.closeButton?.focus({ preventScroll: true });
      }
      this.updateFullscreenButton();
    }

    async toggleFullscreen() {
      if (!this.dialog.open || !this.stage) return;
      if (this.expanded) {
        this.exitFullscreen();
        return;
      }
      if (this.fullscreenRequest || this.fullscreenExit) return;
      this.expanded = true;
      this.updatePresentation();
      // Dialog elements cannot request fullscreen. Use the complete player inside it.
      // The expanded modal also works when fullscreen is unavailable in an editor iframe.
      if (!this.stage.requestFullscreen || !document.fullscreenEnabled) return;
      const stage = this.stage;
      try {
        this.fullscreenRequest = stage.requestFullscreen();
        await this.fullscreenRequest;
        if ((!this.isConnected || !this.dialog.open || !this.expanded) && document.fullscreenElement === stage) {
          await this.releaseFullscreen();
        }
      } catch {
        // Keep the viewport-sized player when the browser declines native fullscreen.
      } finally {
        this.fullscreenRequest = null;
      }
    }

    releaseFullscreen() {
      this.expanded = false;
      const stage = this.stage;
      if (stage && (this.fullscreenRequest || document.fullscreenElement === stage) && !this.fullscreenExit) {
        this.fullscreenExit = Promise.resolve(this.fullscreenRequest).catch(() => {}).then(() => {
          if (document.fullscreenElement === stage) return document.exitFullscreen();
        }).catch(() => {}).finally(() => {
          this.fullscreenExit = null;
        });
      }
      return this.fullscreenExit;
    }

    exitFullscreen() {
      this.releaseFullscreen();
      this.updatePresentation();
      (this.menuButton || this.fullscreenButton)?.focus({ preventScroll: true });
    }

    updateFullscreenButton() {
      if (!this.fullscreenButton) return;
      const expanded = Boolean(this.expanded);
      const label = expanded ? 'Exit full screen' : 'Enter full screen';
      this.fullscreenButton.dataset.state = expanded ? 'fullscreen' : 'windowed';
      this.fullscreenButton.setAttribute('aria-pressed', String(expanded));
      this.fullscreenButton.setAttribute('aria-label', label);
      this.fullscreenButton.title = label;
      const text = this.fullscreenButton.querySelector('[data-story-fullscreen-label]');
      if (text) text.textContent = label;
    }

    updatePlayback() {
      const video = this.video;
      const playing = Boolean(video && !video.paused && !video.ended);
      if (this.resume) {
        this.resume.hidden = playing || !video || (this.error && !this.error.hidden);
        this.resume.setAttribute('aria-label', video?.ended ? 'Replay video' : 'Play video');
        const label = this.resume.querySelector('[data-story-resume-label]');
        if (label) label.textContent = video?.ended ? 'Replay' : 'Play';
      }
      if (playing && this.needsPlay) {
        this.needsPlay = false;
        if (this.status) this.status.hidden = true;
      }
      if (this.toggle) {
        this.toggle.dataset.state = playing ? 'playing' : 'paused';
        this.toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        const text = this.toggle.querySelector('[data-story-toggle-label]');
        if (text) text.textContent = playing ? 'Pause video' : 'Play video';
      }
      if (this.mute && video) {
        this.mute.dataset.state = video.muted ? 'muted' : 'audible';
        this.mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        this.mute.setAttribute('aria-pressed', String(video.muted));
      }
      const fraction = video && Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration : 0;
      if (this.progress) this.progress.style.transform = `scaleX(${Math.max(0, Math.min(1, fraction))})`;
    }

    async shareProduct() {
      if (!this.shop) return;
      const url = this.shop.href;
      const title = this.dialog.querySelector('.nirobi-stories__product-title')?.textContent.trim() || document.title;
      this.video?.pause();
      try {
        if (navigator.share) await navigator.share({ title, url });
        else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          if (this.status && this.dialog.open) {
            this.status.textContent = 'Product link copied';
            this.status.hidden = false;
          }
        } else if (this.status) {
          this.status.textContent = 'Use your browser’s Share option to share this product.';
          this.status.hidden = false;
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.status && this.dialog.open) {
          this.status.textContent = 'Use your browser’s Share option to share this product.';
          this.status.hidden = false;
        }
      }
    }

    clearPlayer() {
      this.mediaEvents?.abort();
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        this.video.load();
        this.video = null;
      }
      this.player?.replaceChildren();
      this.updatePlayback();
    }

    closeStory(restoreFocus = true) {
      this.restoreFocusOnClose = restoreFocus &&
        (this.dialog.dataset.presentation === 'modal' || this.dialog.contains(document.activeElement));
      if (this.dialog.open) this.dialog.close();
      return this.finishClose();
    }

    finishClose() {
      this.setMenu(false);
      const fullscreenExit = this.releaseFullscreen();
      this.clearPlayer();
      this.triggers?.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      if (this.error) this.error.hidden = true;
      if (this.status) this.status.hidden = true;
      const origin = this.origin;
      if (this.restoreFocusOnClose && origin?.isConnected) {
        if (fullscreenExit) {
          fullscreenExit.then(() => {
            if (!this.dialog.open && origin.isConnected &&
              (document.activeElement === document.body || this.dialog.contains(document.activeElement))) {
              origin.focus({ preventScroll: true });
            }
          });
        } else origin.focus({ preventScroll: true });
      }
      this.origin = null;
      return fullscreenExit;
    }
  }

  customElements.define('nirobi-product-stories', NirobiProductStories);
})();
